import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ShieldAlert,
  Crosshair,
  Layers,
  Activity,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  apiClient,
  CryptoAIBriefResponse,
  Core7BriefItem,
  TacticalSpaceItem,
} from '../../api/client';
import {
  CANONICAL_SPOT_SYMBOLS,
  CanonicalSpotSymbol,
  VENUE_METADATA,
  toShortSymbol,
} from '../../shared/constants/watchlist';
import { compareDecimalStrings } from '../../shared/domain/decimal';
import { formatFinancialPrice } from '../../shared/domain/formatters';
import { CRYPTO_BILINGUAL } from '../../shared/constants/bilingualTitles';

export type TacticalTimeframe = '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d';

const TIMEFRAME_LIST: TacticalTimeframe[] = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'];

export type FormationType =
  | 'MOMENTUM_BREAKOUT'
  | 'GRID_RANGE_BOUND'
  | 'LIQUIDITY_AMBUSH'
  | 'DEAD_ORDER_PRUNING';

interface ExtendedCore7BriefItem extends Core7BriefItem {
  support_price?: string | null;
  resistance_price?: string | null;
  risk_reward_ratio?: string | null;
  invalidation_condition?: string | null;
  is_falsified?: boolean | null;
  tactical_bias?: string | null;
  suggested_action_zone?: string | null;
  tactical_spaces?: Record<string, TacticalSpaceItem>;
  tactical_space_metadata?: {
    unavailable_reason?: string | null;
    anchor_timeframe?: string;
    falsification_reason?: string;
    vp_poc?: string;
    vp_val?: string;
    vp_vah?: string;
    profile_1h_val?: string | null;
    profile_1h_poc?: string | null;
    profile_1h_vah?: string | null;
    profile_4h_val?: string | null;
    profile_4h_poc?: string | null;
    profile_4h_vah?: string | null;
    evaluation_timeframe?: string | null;
    evaluation_candle_close_utc?: string | null;
    is_5m_eligible?: boolean | null;
    support_source_timeframe?: string | null;
    support_source_close_utc?: string | null;
    resistance_source_timeframe?: string | null;
    resistance_source_close_utc?: string | null;
    structural_window_bars?: number;
    falsification_trigger_price?: string | null;
  } | null;
}

function getVolumeProfileLevels(
  item: ExtendedCore7BriefItem | undefined,
  timeframe: TacticalTimeframe
) {
  const horizonSpace = item?.tactical_spaces?.[timeframe];
  if (horizonSpace?.volume_profile) {
    return {
      poc: horizonSpace.volume_profile.poc,
      val: horizonSpace.volume_profile.val,
      vah: horizonSpace.volume_profile.vah,
      anchor: timeframe.toUpperCase(),
      realized_area_pct: horizonSpace.volume_profile.realized_value_area_pct,
    };
  }

  return { poc: null, val: null, vah: null, anchor: timeframe.toUpperCase(), realized_area_pct: null };
}

/**
 * Calculates (Resistance - Spot) / (Spot - Support) with pure BigInt decimal precision.
 * Returns exact decimal string (e.g. "1.52") or null if invalid or spot is outside range.
 * Zero binary floating point operations (0 parseFloat / 0 Number math).
 */
function calculateDecimalRiskRewardRatio(
  spotRaw: unknown,
  supportRaw: unknown,
  resistanceRaw: unknown
): string | null {
  if (spotRaw === null || spotRaw === undefined || supportRaw === null || supportRaw === undefined || resistanceRaw === null || resistanceRaw === undefined) {
    return null;
  }
  const strSpot = String(spotRaw).replace(/,/g, '').trim();
  const strSupp = String(supportRaw).replace(/,/g, '').trim();
  const strRes = String(resistanceRaw).replace(/,/g, '').trim();
  if (!strSpot || !strSupp || !strRes || strSpot === '--' || strSupp === '--' || strRes === '--') {
    return null;
  }

  const [sInt = '0', sFrac = ''] = strSpot.split('.');
  const [supInt = '0', supFrac = ''] = strSupp.split('.');
  const [resInt = '0', resFrac = ''] = strRes.split('.');

  const scale = Math.max(sFrac.length, supFrac.length, resFrac.length, 8);
  const factor = 10n ** BigInt(scale);

  const sBig = BigInt(sInt) * factor + BigInt(sFrac.padEnd(scale, '0'));
  const supBig = BigInt(supInt) * factor + BigInt(supFrac.padEnd(scale, '0'));
  const resBig = BigInt(resInt) * factor + BigInt(resFrac.padEnd(scale, '0'));

  if (sBig <= supBig || resBig <= sBig) {
    return null;
  }

  const riskBig = sBig - supBig;
  const rewardBig = resBig - sBig;
  if (riskBig <= 0n) return null;

  // Scale by 100 for 2 fraction digits with half-up rounding
  const rrScaled = (rewardBig * 100n + (riskBig / 2n)) / riskBig;
  const intPart = rrScaled / 100n;
  const fracPart = rrScaled % 100n;
  return `${intPart}.${fracPart.toString().padStart(2, '0')}`;
}

export const TacticalCombatDeckView: React.FC = () => {
  const [selectedSymbol, setSelectedSymbol] = useState<CanonicalSpotSymbol>(CANONICAL_SPOT_SYMBOLS[0]);
  const [selectedTimeframe, setSelectedTimeframe] = useState<TacticalTimeframe>('5m');
  const [data, setData] = useState<CryptoAIBriefResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef<boolean>(true);

  // Poll brief data every 15s with pause on visibility change
  const fetchBrief = useCallback(async (isManual: boolean = false) => {
    if (isManual) setLoading(true);
    try {
      const response = await apiClient.getCryptoBrief();
      if (!isMountedRef.current) return;
      setData(response);
      setError(null);
    } catch (err: unknown) {
      if (!isMountedRef.current) return;
      const msg = err instanceof Error ? err.message : '数据请求失败';
      setError(msg);
      setData(null);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchBrief();

    let timer: NodeJS.Timeout | null = setInterval(() => {
      if (!document.hidden) {
        fetchBrief();
      }
    }, 15000);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      } else {
        fetchBrief();
        if (!timer) {
          timer = setInterval(() => fetchBrief(), 15000);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMountedRef.current = false;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchBrief]);

  // Current active symbol brief data
  const currentSymbolBrief: ExtendedCore7BriefItem | undefined = useMemo(() => {
    if (!data?.core7_briefs) return undefined;
    const normalized = selectedSymbol.replace('/', '-').toUpperCase();
    return data.core7_briefs.find((b) => {
      const bNorm = b.symbol.replace('/', '-').toUpperCase();
      return bNorm === normalized || bNorm === selectedSymbol;
    }) as ExtendedCore7BriefItem | undefined;
  }, [data, selectedSymbol]);

  // Dynamically resolve Volume Profile levels (POC / VAL / VAH) for the active timeframe
  const vpLevels = useMemo(
    () => getVolumeProfileLevels(currentSymbolBrief, selectedTimeframe),
    [currentSymbolBrief, selectedTimeframe]
  );

  // Active tactical space for the selected horizon
  const activeTacticalSpace = useMemo(() => {
    return currentSymbolBrief?.tactical_spaces?.[selectedTimeframe];
  }, [currentSymbolBrief, selectedTimeframe]);

  // Deduce recommended combat formation based on current symbol telemetry & falsification state
  const formationEvaluation = useMemo(() => {
    if (error || !currentSymbolBrief || !currentSymbolBrief.spot_price) {
      return {
        activeFormation: null as FormationType | null,
        reason: error ? `数据链路断开: ${error}` : '标的遥测数据脱机或未就绪 (DATA UNAVAILABLE)',
        isFalsified: false,
      };
    }

    const isFalsified = activeTacticalSpace?.is_falsified === true;
    if (isFalsified) {
      const cond = activeTacticalSpace?.invalidation_condition || 'UNAVAILABLE';
      return {
        activeFormation: 'DEAD_ORDER_PRUNING' as FormationType,
        reason: `${selectedTimeframe} 假说已证伪 (${cond})，强制执行防死单撤离令`,
        isFalsified: true,
      };
    }

    const unavailReason = activeTacticalSpace?.unavailable_reason;
    if (unavailReason === 'UNCONFIRMED_LEVELS_OUTSIDE_PROFILE') {
      return {
        activeFormation: 'GRID_RANGE_BOUND' as FormationType,
        reason: '标的处于 [VAL, VAH] 价值区箱体内，外侧摆动位收敛，区间网格固守阵激活 (低吸高抛)',
        isFalsified: false,
      };
    }
    if (unavailReason) {
      return {
        activeFormation: null as FormationType | null,
        reason: `${selectedTimeframe} 时态结构数据不可用 (${unavailReason})`,
        isFalsified: false,
      };
    }

    const rrStr = activeTacticalSpace?.risk_reward_ratio || null;
    const isHighRR = rrStr ? compareDecimalStrings(rrStr, '2.00') >= 0 : false;
    const regime = (currentSymbolBrief.volume_profile_regime || '').toUpperCase();
    const bias = (currentSymbolBrief.tactical_bias || '').toUpperCase();

    if (
      isHighRR &&
      (regime.includes('EXPANSION') ||
        regime.includes('BREAKOUT') ||
        regime.includes('TREND') ||
        bias.includes('突破') ||
        bias.includes('BREAKOUT'))
    ) {
      return {
        activeFormation: 'MOMENTUM_BREAKOUT' as FormationType,
        reason: `偏多突破共振，盈亏比 ${rrStr || '2.00+'} ≥ 2.0，动能冲锋阵激活`,
        isFalsified: false,
      };
    }

    if (regime.includes('AMBUSH') || regime.includes('EXTREME') || regime.includes('POC') || bias.includes('回踩') || bias.includes('伏击')) {
      return {
        activeFormation: 'LIQUIDITY_AMBUSH' as FormationType,
        reason: '关键筹码密集区峰值收敛，流动性伏击阵就绪',
        isFalsified: false,
      };
    }

    if (regime.includes('RANGE') || regime.includes('STABLE') || regime.includes('COMPRESSION') || bias.includes('震荡') || bias.includes('网格')) {
      const gridType: FormationType = 'GRID_RANGE_BOUND';
      return {
        activeFormation: gridType,
        reason: '处于 [VAL, VAH] 箱体内部震荡，区间网格固守阵坚守',
        isFalsified: false,
      };
    }

    return {
      activeFormation: null as FormationType | null,
      reason: currentSymbolBrief.tactical_bias
        ? `战术意向: ${currentSymbolBrief.tactical_bias} (观察中)`
        : '未检测到明确阵型特征 (OBSERVING)',
      isFalsified: false,
    };
  }, [currentSymbolBrief, activeTacticalSpace, selectedTimeframe, error]);

  // Overall market macro state label
  const macroVerdictText = useMemo(() => {
    if (error || !data?.macro_climate) return '行情状态探测中 (UNAVAILABLE)';
    const regime = data.macro_climate.primary_regime || 'UNAVAILABLE';
    const align = data.cross_layer_alignment?.alignment_verdict || '';
    if (align === 'STRONG_ALIGNMENT') return '偏多共振态势 · 主升浪主导';
    if (align === 'STRONG_BEARISH_ALIGNMENT') return '偏空共振态势 · 防守破位主导';
    if (regime.includes('EXPANSION')) return '变盘前夜 · 波动率急剧扩张';
    if (regime.includes('COMPRESSION')) return '宽幅震荡 · 筹码蓄势收敛';
    return data.macro_climate.regime_description || 'UNAVAILABLE';
  }, [data, error]);

  // Data status indicator badge
  const renderStatusBadge = () => {
    const state = error ? 'disconnected' : data?.telemetry_state || (loading ? 'loading' : 'fresh');
    switch (state) {
      case 'fresh':
        return (
          <span className="badge badge-bullish" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--color-bullish)' }}></span>
            极鲜 FRESH
          </span>
        );
      case 'stale':
        return (
          <span className="badge badge-warning" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--color-warning)' }}></span>
            延迟 STALE
          </span>
        );
      case 'disconnected':
        return (
          <span className="badge badge-bearish" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--color-bearish)' }}></span>
            离线 DISCONNECTED
          </span>
        );
      case 'empty':
        return (
          <span className="badge badge-neutral" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            空仓 EMPTY
          </span>
        );
      case 'loading':
      default:
        return (
          <span className="badge badge-info" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            加载中 LOADING
          </span>
        );
    }
  };

  return (
    <div
      className="tactical-combat-deck-view"
      id="view-crypto-tactical-deck"
      role="region"
      aria-label="加密货币战术牌面与出战中枢"
      style={{ width: '100%', padding: '0', margin: '0' }}
    >
      {/* ================================================================
          TIER 1: VIEW HEADER (Dual-Language SSOT Hierarchy)
          ================================================================ */}
      <header className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <h1
              className="bilingual-header-title-zh"
              style={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: 'var(--text-primary)',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span className="section-symbol">♟️</span>
              <span>{CRYPTO_BILINGUAL.tacticalDeck.zh}</span>
            </h1>
            <span className="terminal-badge">STAGE 106</span>
            {renderStatusBadge()}
          </div>
          <span className="bilingual-header-subtitle-en">
            {CRYPTO_BILINGUAL.tacticalDeck.en}
          </span>
          <p className="view-subtitle font-mono" style={{ margin: '3px 0 0 0' }}>
            Core-7 标的多周期行情牌面诊断、Volume Profile 筹码重心与 4 大出战阵型决策中枢
          </p>
        </div>
      </header>

      {/* Disconnection Warning Alert */}
      {error && (
        <div
          className="card"
          style={{
            backgroundColor: 'var(--alert-bearish-bg)',
            border: '1px solid var(--alert-bearish-border)',
            borderRadius: '6px',
            padding: '10px 14px',
            marginBottom: '16px',
            color: 'var(--color-bearish)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
          }}
        >
          <ShieldAlert size={16} />
          <div>
            <strong>链路异常</strong>: {error}。正在自动进行重连尝试...
          </div>
        </div>
      )}

      {/* ================================================================
          TIER 1 & 2: 一体化战术指挥控制台 (.card .tactical-command-hud)
          一体化整合标的切换、5M哨兵状态、宏观定调与周期切片，彻底消除双层冗余与高度浪费
          ================================================================ */}
      <section
        className="card tactical-command-hud"
        style={{
          background: 'var(--bg-surface-elevated)',
          border: '1px solid var(--border-card)',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
        aria-label="战术指挥控制台"
      >
        {/* Row 1: 标的走马灯 + 哨兵雷达状态融合 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div
            className="crypto-ticker-bar"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}
            role="tablist"
            aria-label="Core-7 战术标的切换与哨兵状态"
          >
            {CANONICAL_SPOT_SYMBOLS.map((sym, idx) => {
              const norm = sym.replace('/', '-').toUpperCase();
              const briefItem = data?.core7_briefs?.find(
                (b) => b.symbol.replace('/', '-').toUpperCase() === norm
              ) as ExtendedCore7BriefItem | undefined;
              const isFalsified = briefItem?.is_falsified;
              const isSelected = selectedSymbol === sym;
              const meta = VENUE_METADATA[sym];
              const short = toShortSymbol(sym);

              const indicatorColor =
                isFalsified === true
                  ? 'var(--color-bearish)'
                  : isFalsified === false
                  ? 'var(--color-bullish)'
                  : 'var(--text-muted)';

              const statusTip =
                isFalsified === true
                  ? '5m 哨兵: 已破位 BREACHED'
                  : isFalsified === false
                  ? '5m 哨兵: 结构完好 INTACT'
                  : '5m 哨兵: 未就绪';

              return (
                <button
                  key={sym}
                  role="tab"
                  aria-selected={isSelected}
                  onClick={() => setSelectedSymbol(sym)}
                  className={`crypto-ticker-pill ${isSelected ? 'active' : ''}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'var(--font-mono)',
                    background: isSelected ? 'var(--bg-surface-hover)' : 'transparent',
                    border: isSelected
                      ? '1px solid var(--accent-cyan)'
                      : '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                    color: isSelected ? 'var(--accent-cyan)' : 'var(--text-primary)',
                    fontWeight: isSelected ? 600 : 400,
                    transition: 'all 0.15s ease',
                  }}
                  title={`${short}/USDT · ${meta?.role || ''} | ${statusTip}`}
                >
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      backgroundColor: indicatorColor,
                      boxShadow: isSelected ? `0 0 6px ${indicatorColor}` : 'none',
                    }}
                  />
                  <span>{short}/USDT</span>
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>[{idx + 1}]</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 2: 宏观定调 (严格左对齐排版) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            🎴 宏观定调:
          </span>
          <span
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--text-primary)',
              background: 'var(--bg-card)',
              padding: '2px 8px',
              borderRadius: '4px',
              border: '1px solid var(--border-subtle)',
            }}
          >
            {macroVerdictText}
          </span>
          <span className="badge badge-info" style={{ fontSize: '10px' }}>
            {data?.cross_layer_alignment?.alignment_verdict || 'UNAVAILABLE'}
          </span>
        </div>

        {/* Row 2: 时间周期切片 + 实时标的遥测上下文 */}
        <div
          className="timeframe-selector"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            borderTop: '1px solid var(--border-subtle)',
            paddingTop: '8px',
            flexWrap: 'wrap',
          }}
          role="tablist"
          aria-label="时间周期与监控上下文"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', marginRight: '2px' }}>
              周期切片:
            </span>
            {TIMEFRAME_LIST.map((tf) => {
              const isActive = selectedTimeframe === tf;
              return (
                <button
                  key={tf}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setSelectedTimeframe(tf)}
                  className={`timeframe-pill ${isActive ? 'active' : ''}`}
                  style={{
                    background: isActive ? 'var(--accent-cyan)' : 'var(--bg-surface-hover)',
                    color: isActive ? 'var(--text-inverse)' : 'var(--text-secondary)',
                    border: isActive ? '1px solid var(--accent-cyan)' : '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: '4px',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {tf}
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '11px', color: 'var(--text-secondary)' }}>
            <span>
              标的: <strong style={{ color: 'var(--text-primary)' }}>{selectedSymbol}</strong>
            </span>
            <span>
              现货: <strong style={{ color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)' }}>
                {currentSymbolBrief?.spot_price ? `$${formatFinancialPrice(currentSymbolBrief.spot_price, selectedSymbol)}` : '--'}
              </strong>
            </span>
            <span>
              {selectedTimeframe.toUpperCase()} POC: <strong style={{ color: 'var(--accent-cyan)', fontFamily: 'var(--font-mono)' }}>
                {vpLevels.poc ? formatFinancialPrice(vpLevels.poc, selectedSymbol) : '--'}
              </strong>
            </span>
            {activeTacticalSpace?.volume_profile?.realized_value_area_pct && (
              <span style={{ color: 'var(--text-muted)' }}>
                [覆盖: {activeTacticalSpace.volume_profile.realized_value_area_pct}%]
              </span>
            )}
          </div>
        </div>
      </section>

      {/* ================================================================
          TIER 3: 双翼实战沙盘 (.deck-split-grid)
          ================================================================ */}
      <section
        className="deck-split-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
          gap: '16px',
          marginBottom: '16px',
        }}
        aria-label="双翼实战沙盘"
      >
        {/* ------------------------------------------------------------
            LEFT WING: 🎴 行情状态牌面 (Market Hand)
            ------------------------------------------------------------ */}
        <div
          className="card market-hand-wing"
          style={{
            background: 'var(--bg-surface-elevated)',
            border: '1px solid var(--border-card)',
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid var(--border-subtle)',
              paddingBottom: '8px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>🎴</span>
              <h2 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                行情状态牌面 · MARKET HAND
              </h2>
            </div>
            <span className="badge badge-info" style={{ fontSize: '10px' }}>
              {selectedTimeframe} CADENCE
            </span>
          </div>

          {/* Hand Card 1: 体制牌 (Regime Card) */}
          <div
            className="card"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '6px',
              padding: '12px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>1. 体制牌 · REGIME CLASSIFICATION</span>
              <span
                className="badge"
                style={{
                  fontSize: '10px',
                  color: 'var(--accent-cyan)',
                  borderColor: 'var(--accent-cyan)',
                }}
              >
                {currentSymbolBrief?.volume_profile_regime || currentSymbolBrief?.tactical_bias || 'UNAVAILABLE'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <strong style={{ fontSize: '15px', color: 'var(--text-primary)' }}>
                {data?.macro_climate?.primary_regime || 'UNAVAILABLE'}
              </strong>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                {currentSymbolBrief?.tactical_bias ? `当前标的战术意图: ${currentSymbolBrief.tactical_bias} · ` : ''}
                {data?.cross_layer_alignment?.diagnostic_summary || '多周期量能分布暂未聚合'}
              </span>
            </div>
          </div>

          {/* Hand Card 2: 筹码牌 (Volume Profile / POC Card) */}
          <div
            className="card"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '6px',
              padding: '12px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                2. 筹码牌 · VALUE AREA &amp; POC ({vpLevels.anchor})
                {vpLevels.realized_area_pct ? ` [覆盖: ${vpLevels.realized_area_pct}%]` : ''}
              </span>
              <span style={{ fontSize: '10px', color: 'var(--color-emerald)', fontFamily: 'var(--font-mono)' }}>
                POC: {vpLevels.poc ? formatFinancialPrice(vpLevels.poc, selectedSymbol) : '--'}
              </span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '8px',
                textAlign: 'center',
                fontSize: '12px',
                fontFamily: 'var(--font-mono)',
              }}
            >
              <div style={{ padding: '6px', background: 'var(--bg-surface)', borderRadius: '4px' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>VAL (价值下沿)</div>
                <strong style={{ color: 'var(--color-emerald)' }}>
                  {vpLevels.val
                    ? formatFinancialPrice(vpLevels.val, selectedSymbol)
                    : '--'}
                </strong>
              </div>
              <div style={{ padding: '6px', background: 'var(--bg-surface)', borderRadius: '4px' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>POC (筹码密集峰)</div>
                <strong style={{ color: 'var(--accent-cyan)' }}>
                  {vpLevels.poc
                    ? formatFinancialPrice(vpLevels.poc, selectedSymbol)
                    : '--'}
                </strong>
              </div>
              <div style={{ padding: '6px', background: 'var(--bg-surface)', borderRadius: '4px' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>VAH (价值上沿)</div>
                <strong style={{ color: 'var(--color-rose)' }}>
                  {vpLevels.vah
                    ? formatFinancialPrice(vpLevels.vah, selectedSymbol)
                    : '--'}
                </strong>
              </div>
            </div>
          </div>

          {/* Hand Card 3: 证伪牌 (Falsification Card) */}
          <div
            className="card"
            style={{
              background: 'var(--bg-card)',
              border: activeTacticalSpace?.is_falsified === true
                ? '1px solid var(--color-bearish)'
                : '1px solid var(--border-subtle)',
              borderRadius: '6px',
              padding: '12px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                3. 证伪牌 · {selectedTimeframe.toUpperCase()} HYPOTHESIS FALSIFICATION
              </span>
              <span
                className="badge"
                style={{
                  fontSize: '10px',
                  backgroundColor: activeTacticalSpace?.is_falsified === true
                    ? 'var(--alert-bearish-bg)'
                    : 'transparent',
                  color: activeTacticalSpace?.is_falsified === true
                    ? 'var(--color-bearish)'
                    : activeTacticalSpace?.is_falsified === false
                    ? 'var(--color-bullish)'
                    : 'var(--text-muted)',
                  borderColor: activeTacticalSpace?.is_falsified === true
                    ? 'var(--color-bearish)'
                    : activeTacticalSpace?.is_falsified === false
                    ? 'var(--color-bullish)'
                    : 'var(--border-subtle)',
                }}
              >
                {activeTacticalSpace?.is_falsified === true
                  ? '🔴 BREACHED'
                  : activeTacticalSpace?.is_falsified === false
                  ? '🟢 STRUCTURE INTACT'
                  : '⚪ UNAVAILABLE'}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
              {activeTacticalSpace?.is_falsified === true ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-bearish)' }}>
                  <XCircle size={15} />
                  <span>
                    破位触发: {activeTacticalSpace?.invalidation_condition || 'UNAVAILABLE'}
                  </span>
                </div>
              ) : activeTacticalSpace?.is_falsified === false ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-bullish)' }}>
                  <CheckCircle2 size={15} />
                  <span>{selectedTimeframe} 闭合柱结构完好，未击穿防守关键支撑位，假说有效</span>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)' }}>
                  <span>{selectedTimeframe} 假说证伪状态暂无有效数据 (UNAVAILABLE)</span>
                </div>
              )}
            </div>
          </div>

          {/* Hand Card 4: 波动率与空间摩擦牌 */}
          <div
            className="card"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '6px',
              padding: '12px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>4. 波动率与空间摩擦牌 · VOLATILITY &amp; FRICTION</span>
              <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>BASIS &amp; SPREAD</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontFamily: 'var(--font-mono)' }}>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>基差偏离: </span>
                <span style={{ color: 'var(--accent-cyan)' }}>
                  {currentSymbolBrief?.basis_bps ? `${currentSymbolBrief.basis_bps} bps` : '--'}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--text-muted)' }}>24h 涨跌: </span>
                <span
                  style={{
                    color: (currentSymbolBrief?.change_24h_pct || '').startsWith('-')
                      ? 'var(--color-bearish)'
                      : (currentSymbolBrief?.change_24h_pct)
                      ? 'var(--color-bullish)'
                      : 'var(--text-muted)',
                  }}
                >
                  {currentSymbolBrief?.change_24h_pct ? currentSymbolBrief.change_24h_pct : '--'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ------------------------------------------------------------
            RIGHT WING: ⚔️ 建议出战牌阵 (Combat Formations)
            ------------------------------------------------------------ */}
        <div
          className="card combat-formation-wing"
          style={{
            background: 'var(--bg-surface-elevated)',
            border: '1px solid var(--border-card)',
            borderRadius: '8px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid var(--border-subtle)',
              paddingBottom: '8px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>⚔️</span>
              <h2 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                建议出战牌阵 · COMBAT FORMATIONS
              </h2>
            </div>
            <span
              className="badge"
              style={{
                fontSize: '10px',
                color: formationEvaluation.isFalsified
                  ? 'var(--color-bearish)'
                  : formationEvaluation.activeFormation
                  ? 'var(--color-bullish)'
                  : 'var(--text-muted)',
                borderColor: formationEvaluation.isFalsified
                  ? 'var(--color-bearish)'
                  : formationEvaluation.activeFormation
                  ? 'var(--color-bullish)'
                  : 'var(--border-subtle)',
              }}
            >
              {formationEvaluation.activeFormation || 'UNAVAILABLE'}
            </span>
          </div>

          {/* 4 Formations Showcase Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
            {/* Formation 1: MOMENTUM_BREAKOUT */}
            <div
              className="card formation-card"
              style={{
                padding: '10px',
                borderRadius: '6px',
                background:
                  formationEvaluation.activeFormation === 'MOMENTUM_BREAKOUT'
                    ? 'var(--bg-surface)'
                    : 'var(--bg-card)',
                border:
                  formationEvaluation.activeFormation === 'MOMENTUM_BREAKOUT'
                    ? '1px solid var(--color-bullish)'
                    : '1px solid var(--border-subtle)',
                boxShadow: 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <span>🏹</span>
                <strong style={{ fontSize: '12px', color: 'var(--color-bullish)' }}>动能突击冲锋阵</strong>
              </div>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                顺势进攻，追击突破，严格止损。适用偏多强共振且 R:R ≥ 2.0。
              </p>
            </div>

            {/* Formation 2: GRID_RANGE_BOUND */}
            <div
              className="card formation-card"
              style={{
                padding: '10px',
                borderRadius: '6px',
                background:
                  formationEvaluation.activeFormation === 'GRID_RANGE_BOUND'
                    ? 'var(--bg-surface)'
                    : 'var(--bg-card)',
                border:
                  formationEvaluation.activeFormation === 'GRID_RANGE_BOUND'
                    ? '1px solid var(--accent-cyan)'
                    : '1px solid var(--border-subtle)',
                boxShadow:
                  formationEvaluation.activeFormation === 'GRID_RANGE_BOUND'
                    ? '0 0 10px rgba(0, 229, 255, 0.15)'
                    : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <span>🛡️</span>
                <strong style={{ fontSize: '12px', color: 'var(--accent-cyan)' }}>区间网格固守阵</strong>
                {formationEvaluation.activeFormation === 'GRID_RANGE_BOUND' && (
                  <span className="badge badge-bullish" style={{ fontSize: '9px', padding: '1px 5px', marginLeft: 'auto' }}>
                    当前激活
                  </span>
                )}
              </div>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                底部低吸，顶部高抛，绝不追单。处于 [VAL, VAH] 箱体内部震荡。
              </p>
            </div>

            {/* Formation 3: LIQUIDITY_AMBUSH */}
            <div
              className="card formation-card"
              style={{
                padding: '10px',
                borderRadius: '6px',
                background:
                  formationEvaluation.activeFormation === 'LIQUIDITY_AMBUSH'
                    ? 'var(--bg-surface)'
                    : 'var(--bg-card)',
                border:
                  formationEvaluation.activeFormation === 'LIQUIDITY_AMBUSH'
                    ? '1px solid var(--color-warning)'
                    : '1px solid var(--border-subtle)',
                boxShadow: 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <span>🪤</span>
                <strong style={{ fontSize: '12px', color: 'var(--color-warning)' }}>流动性伏击阵</strong>
              </div>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                关键筹码密集区挂单埋伏，极高盈亏比。逼近极端 POC 峰值或回踩带。
              </p>
            </div>

            {/* Formation 4: DEAD_ORDER_PRUNING */}
            <div
              className="card formation-card"
              style={{
                padding: '10px',
                borderRadius: '6px',
                background:
                  formationEvaluation.activeFormation === 'DEAD_ORDER_PRUNING'
                    ? 'var(--alert-bearish-bg)'
                    : 'var(--bg-card)',
                border:
                  formationEvaluation.activeFormation === 'DEAD_ORDER_PRUNING'
                    ? '1px solid var(--color-bearish)'
                    : '1px solid var(--border-subtle)',
                boxShadow: 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <span>🚫</span>
                <strong style={{ fontSize: '12px', color: 'var(--color-bearish)' }}>防死单撤离令</strong>
              </div>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                强制取消挂单，紧咬止损。5m 假说被打穿或体制发生反转。
              </p>
            </div>
          </div>

          {/* Tactical Action & Position Parameters Panel */}
          <div
            className="card"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              borderRadius: '6px',
              padding: '14px',
              marginTop: '4px',
            }}
          >
            <div
              style={{
                fontSize: '11px',
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                marginBottom: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Crosshair size={13} />
              <span>当前标的出战实战参数 · COMBAT PARAMETERS</span>
            </div>

            {(() => {
              const isOutsideProfile = activeTacticalSpace?.unavailable_reason === 'UNCONFIRMED_LEVELS_OUTSIDE_PROFILE';
              const effectiveSupport = activeTacticalSpace?.support_price
                ?? (isOutsideProfile ? activeTacticalSpace?.volume_profile?.val : null);
              const supportLabel = activeTacticalSpace?.support_price
                ? ''
                : (isOutsideProfile && activeTacticalSpace?.volume_profile?.val)
                ? ' (VAL)'
                : '';

              const effectiveResistance = activeTacticalSpace?.resistance_price
                ?? (isOutsideProfile ? activeTacticalSpace?.volume_profile?.vah : null);
              const resistanceLabel = activeTacticalSpace?.resistance_price
                ? ''
                : (isOutsideProfile && activeTacticalSpace?.volume_profile?.vah)
                ? ' (VAH)'
                : '';

              const dynamicRR = activeTacticalSpace?.risk_reward_ratio
                ?? calculateDecimalRiskRewardRatio(
                    currentSymbolBrief?.spot_price,
                    effectiveSupport,
                    effectiveResistance
                  );

              const invalidationText = activeTacticalSpace?.is_falsified
                ? '已触发防守破位，立即撤单止损'
                : activeTacticalSpace?.invalidation_condition
                ? activeTacticalSpace.invalidation_condition
                : isOutsideProfile && effectiveSupport && effectiveResistance
                ? `箱体突破失效: 跌破 VAL ${formatFinancialPrice(effectiveSupport, selectedSymbol)} 或上破 VAH ${formatFinancialPrice(effectiveResistance, selectedSymbol)}`
                : activeTacticalSpace?.unavailable_reason
                ? `结构待确认 (${activeTacticalSpace.unavailable_reason})`
                : '--';

              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>战术防守位 (Support)</div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-emerald)', fontFamily: 'var(--font-mono)' }}>
                      {effectiveSupport
                        ? `${formatFinancialPrice(effectiveSupport, selectedSymbol)}${supportLabel}`
                        : '--'}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>进攻目标位 (Resistance)</div>
                    <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-rose)', fontFamily: 'var(--font-mono)' }}>
                      {effectiveResistance
                        ? `${formatFinancialPrice(effectiveResistance, selectedSymbol)}${resistanceLabel}`
                        : '--'}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>真实空间盈亏比 (R:R)</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                      <span
                        className="badge badge-bullish font-mono"
                        style={{ fontSize: '13px', fontWeight: 700 }}
                      >
                        {dynamicRR ? `${dynamicRR} : 1` : '--'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>失效撤单条件 (Invalidation)</div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: activeTacticalSpace?.is_falsified ? 'var(--color-bearish)' : 'var(--text-secondary)',
                        marginTop: '2px',
                        lineHeight: '1.3',
                      }}
                    >
                      {invalidationText}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div
              style={{
                marginTop: '12px',
                paddingTop: '8px',
                borderTop: '1px solid var(--border-subtle)',
                fontSize: '11px',
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Activity size={12} />
              <span>决策依据: {formationEvaluation.reason}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          TIER 4: 底部 TSO 3.0 机器协同对齐栏 (.card .tso-grounding-drawer)
          ================================================================ */}
      <footer
        className="card tso-grounding-drawer"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-card)',
          borderRadius: '8px',
          padding: '12px 18px',
          fontSize: '12px',
          color: 'var(--text-muted)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
        }}
        aria-label="TSO 3.0 机器协同对齐栏"
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Layers size={14} style={{ color: 'var(--accent-cyan)' }} />
          <span>
            <strong>TSO 3.0 协同中枢</strong>: 当前战术牌面与推荐出战牌阵参数已与 TSO 3.0 Sentinel 决策队列 100% 同源对齐
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', fontFamily: 'var(--font-mono)', fontSize: '11px' }}>
          <span>因果时延 W ≥ 3s (已闭环)</span>
          <span>纯 Decimal 零浮点 (AST 校验)</span>
          <span>Zero-Mock 诚实 5 态流转</span>
        </div>
      </footer>
    </div>
  );
};
export default TacticalCombatDeckView;
